<template>
  <div v-if="alerts.length > 0" ref="rootEl" class="system-alerts-bell">
    <a href="#" class="alert-trigger" @click.prevent="toggle">
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
      <span class="badge">{{ alerts.length > 99 ? '99+' : alerts.length }}</span>
    </a>

    <div v-if="isOpen" class="alerts-dropdown" :class="themeClass">
      <div class="dropdown-header">
        <span class="header-title">Alerts</span>
      </div>

      <div class="dropdown-content">
        <div class="alerts-list">
          <div
            v-for="alert in alerts"
            :key="alert.key"
            class="alert-item"
            :class="{ clickable: !!alert.route }"
            @click="onAlertClick(alert)"
          >
            <div class="priority-indicator" :class="alert.critical ? 'priority-critical' : 'priority-warning'"></div>
            <div class="alert-content">
              <div class="alert-title">{{ alert.title }}</div>
              <div v-if="alert.text" class="alert-text">{{ alert.text }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'

const props = defineProps<{
  groups: any[]
  trialDaysRemaining: number
}>()

const inst = getCurrentInstance()

const isOpen = ref(false)
const isLightMode = ref(false)
const rootEl = ref<HTMLElement | null>(null)

const alerts = computed(() => {
  const items: any[] = []

  for (const g of props.groups || []) {
    if (Number(g.email_dns_alert) === 1) {
      items.push({
        key: 'email-dns-' + g.id,
        title: 'Email domain failed verification',
        text: g.name + ': ' + g.email_domain + ' DNS records are failing. Automated emails fall back to the default sender and campaigns are paused until the records are restored. Click to review.',
        critical: true,
        route: '/mail/settings'
      })
    }

    if (Number(g.suppressed_staff_count) > 0) {
      const n = Number(g.suppressed_staff_count)
      const who = n === 1 ? '1 staff member' : n + ' staff members'
      items.push({
        key: 'email-suppressed-' + g.id,
        title: 'Staff email delivery blocked',
        text: g.name + ': notification emails to ' + who + ' are blocked because their email provider reported a past message as spam. They can restore delivery from their own profile page. Click to review your staff list.',
        critical: false,
        route: '/settings/user-management'
      })
    }
  }

  if (props.trialDaysRemaining === 0) {
    items.push({
      key: 'trial',
      title: 'Your trial ends today',
      text: '',
      critical: false,
      route: null
    })
  } else if (props.trialDaysRemaining > 0) {
    const days = props.trialDaysRemaining === 1 ? '1 day' : props.trialDaysRemaining + ' days'
    items.push({
      key: 'trial',
      title: 'Your trial ends in ' + days,
      text: '',
      critical: false,
      route: null
    })
  }

  return items
})

const themeClass = computed(() => (isLightMode.value ? 'theme-light' : 'theme-dark'))

function toggle() {
  isLightMode.value = document.body.classList.contains('white-content')
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

function onAlertClick(alert: any) {
  if (!alert.route) return
  close()
  const router = inst?.proxy?.$router
  if (router && router.currentRoute.path !== alert.route) {
    router.push(alert.route)
  }
}

function onDocumentClick(event: MouseEvent) {
  const el = rootEl.value
  if (el && !(el === event.target || el.contains(event.target as Node))) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})
</script>

<style scoped>
/* Popover shell mirrors AnnouncementsBell.vue so the navbar dropdowns read as one system */
.system-alerts-bell {
  position: relative;
  display: inline-block;
}

.alert-trigger {
  font-size: 18px;
  position: relative;
  padding: 0;
  transition: color 0.2s;
  color: inherit;
  display: flex;
  align-items: center;
}

.alert-trigger:hover {
  color: #e14eca;
}

.badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  padding: 0 4px;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.alerts-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  border-radius: 8px;
  z-index: 1000;
  margin-top: 0.5rem;
}

/* Dark mode (default) */
.alerts-dropdown.theme-dark {
  background: #22233a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.7), 0 2px 10px rgba(0, 0, 0, 0.5);
}

.theme-dark .dropdown-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.theme-dark .header-title {
  color: #fff;
}

.theme-dark .alert-item.clickable:hover {
  background: rgba(255, 255, 255, 0.1);
}

.theme-dark .alert-title {
  color: #fff;
}

.theme-dark .alert-text {
  color: #adb5bd;
}

/* Light mode */
.alerts-dropdown.theme-light {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
}

.theme-light .dropdown-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.theme-light .header-title {
  color: #344675;
}

.theme-light .alert-item.clickable:hover {
  background: rgba(0, 0, 0, 0.05);
}

.theme-light .alert-title {
  color: #344675;
}

.theme-light .alert-text {
  color: #6c757d;
}

/* Shared styles */
.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.header-title {
  font-weight: 600;
}

.dropdown-content {
  max-height: 300px;
  overflow-y: auto;
}

.alerts-list {
  padding: 0.5rem 0;
}

.alert-item {
  display: flex;
  padding: 0.75rem 1rem;
  transition: background 0.2s;
}

.alert-item.clickable {
  cursor: pointer;
}

.priority-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.75rem;
  margin-top: 0.4rem;
  flex-shrink: 0;
}

.priority-critical {
  background: #dc3545;
}

.priority-warning {
  background: #ffc107;
}

.alert-content {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-size: 0.9rem;
}

.alert-text {
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
</style>
